/**
 * Lobby Screen — Pre-Game-Lobby mit Spielerliste & Start-Button.
 */
import { div, h2, h3, p, button, el } from '../../utils/domHelpers.js';
import { router } from '../../core/router.js';
import { lobbyService } from '../../services/lobbyService.js';
import { eventBus } from '../../core/eventBus.js';
import { toast } from '../components/toast.js';
import { createChat } from '../components/chat.js';
import { getInitials } from '../../utils/formatters.js';

export function renderLobbyScreen() {
  const lobby = lobbyService.currentLobby;
  if (!lobby) {
    setTimeout(() => router.navigate('/'), 0);
    return div({}, ['Keine aktive Lobby — leite zurück...']);
  }

  let playerListEl, startBtn, statusText;

  function updatePlayerList() {
    const l = lobbyService.currentLobby;
    if (!l) return;
    playerListEl.innerHTML = '';
    for (const p of l.players) {
      const isHost = p.id === l.hostId;
      const isSelf = p.id === lobbyService.playerId;
      const classes = ['player-card'];
      if (isHost) classes.push('player-card--host');
      if (isSelf) classes.push('player-card--self');

      playerListEl.appendChild(
        div({ class: classes.join(' ') }, [
          div({ class: 'player-avatar' }, [getInitials(p.name)]),
          div({ class: 'player-info' }, [
            div({ class: 'player-name' }, [p.name + (isHost ? ' 👑' : '') + (isSelf ? ' (Du)' : '')]),
            div({ class: 'player-score' }, [`#${l.players.indexOf(p) + 1}`])
          ])
        ])
      );
    }

    // Status-Text + Start-Button-Visibility
    statusText.textContent = `${l.players.length}/8 Spieler · ${l.players.length >= 2 ? 'bereit zum Start' : 'warte auf Mitspieler...'}`;
    const canStart = lobbyService.isHost() && l.players.length >= 2;
    startBtn.disabled = !canStart;
    startBtn.style.display = lobbyService.isHost() ? 'inline-flex' : 'none';
  }

  // Event-Subscriptions
  const offs = [
    eventBus.on('server:lobby_updated',   updatePlayerList),
    eventBus.on('server:player_joined',   (m) => { updatePlayerList(); toast.info(`${m.player.name} ist beigetreten`); }),
    eventBus.on('server:player_left',     updatePlayerList),
    eventBus.on('server:game_started',    () => router.navigate('/game')),
    eventBus.on('server:round_started',   () => router.navigate('/game')),
  ];

  // Cleanup beim Verlassen
  eventBus.on('route:before-change', () => offs.forEach(off => off()));

  // === Layout ===
  const codeBox = el('div', { class: 'lobby-code mono',
    title: 'Klicken um zu kopieren',
    onclick: () => {
      navigator.clipboard?.writeText(lobby.code);
      toast.success(`Code ${lobby.code} kopiert`);
    }
  }, [lobby.code]);

  playerListEl = div({ class: 'player-list stagger' });
  statusText = p({ class: 'mono', style: { color: 'var(--text-secondary)', marginBottom: 'var(--sp-4)' } });

  startBtn = button({ class: 'btn btn-primary btn-block btn-lg pulse-glow',
    onclick: () => lobbyService.startGame(5)
  }, ['🚀 Spiel starten']);

  // Chat-Komponente
  const chat = createChat(lobbyService.playerId);

  // Spieler-Join/Leave als System-Messages im Chat
  offs.push(eventBus.on('server:player_joined', (m) => chat.appendSystemMessage(`${m.player.name} ist beigetreten`)));
  offs.push(eventBus.on('server:player_left',   () => chat.appendSystemMessage('Ein Spieler hat die Lobby verlassen')));

  const panel = el('div', { class: 'glass-panel glass-panel--wide fade-up' }, [
    div({ class: 'lobby-header' }, [
      h2({ class: 'glow-text-cyan' }, ['LOBBY']),
      p({ class: 'mono', style: { color: 'var(--text-muted)' } }, ['Code teilen, alle warten auf den Host:']),
      codeBox,
    ]),

    statusText,
    playerListEl,
    chat.node,

    div({ class: 'flex-col gap-3', style: { marginTop: 'var(--sp-4)' } }, [
      startBtn,
      button({ class: 'btn btn-ghost btn-block',
        onclick: () => { lobbyService.leaveLobby(); router.navigate('/'); }
      }, ['Lobby verlassen']),
    ]),
  ]);

  setTimeout(updatePlayerList, 0);

  return panel;
}
